// /admin/groups/:id: one family group: members (make owner, remove), recipes, rename, delete.
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
  deleteGroup,
  exportRecipes,
  getGroup,
  makeGroupOwner,
  removeFromGroup,
  removeRecipeFromGroup,
  renameGroup,
} from '../../../api/adminV2'
import { copyText, formatDate, LoadError, Loading, saveFile, useFeedback, useLoad } from '../ui'

const ROLE_LABEL = { 1: 'Member', 2: 'Admin', 3: 'Owner' }
const OWNER = 3

const Group = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast, toastError, confirm } = useFeedback()
  const { data: g, error, loading, reload, setData } = useLoad(() => getGroup(id), [id])
  const [busy, setBusy] = useState('')

  if (error && !g) return <LoadError error={error} onRetry={reload} />
  if (loading && !g) return <Loading />
  if (!g) return null

  const members = [...(g.users || [])].sort(
    (a, b) => Number(b.pivot?.role || 0) - Number(a.pivot?.role || 0)
  )
  const owner = members.find((u) => Number(u.pivot?.role) === OWNER)
  const recipes = g.recipes || []
  const label = (u) => u.fullname || u.email

  const rename = () =>
    confirm({
      title: 'Rename group',
      action: 'Save',
      initial: g.name || '',
      valid: (v) => (v || '').trim().length >= 2 && v.trim() !== g.name,
      render: (v, setV, ok) => (
        <input
          type="text"
          aria-label="Group name"
          value={v}
          autoFocus
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && ok()}
        />
      ),
      run: (v) => renameGroup(g.id, v.trim()).then(() => v.trim()),
    }).then((name) => {
      if (!name) return
      setData({ ...g, name })
      toast('Group renamed')
    })

  const copyInvite = () =>
    copyText(g.inviteLink)
      .then(() => toast('Invite link copied'))
      .catch(() => toastError(`Could not copy. The link is ${g.inviteLink}`))

  const makeOwner = (u) =>
    confirm({
      title: `Make ${label(u)} the owner?`,
      text: owner
        ? `${label(owner)} stays in the group but is no longer the owner.`
        : 'The owner can manage members and settings.',
      action: 'Make owner',
      run: () => makeGroupOwner(g.id, u.id),
    }).then((ok) => {
      if (!ok) return
      toast(`${label(u)} is now the owner`)
      reload()
    })

  const kick = (u) =>
    confirm({
      title: `Remove ${label(u)} from this group?`,
      text: "They lose access to the group. Their own recipes leave the group with them; everyone else's stay.",
      action: 'Remove',
      danger: true,
      run: () => removeFromGroup(u.id, g.id),
    }).then((ok) => {
      if (!ok) return
      toast(`${label(u)} removed from the group`)
      reload()
    })

  const removeRecipe = (r) =>
    confirm({
      title: `Take "${r.name || 'this recipe'}" out of this group?`,
      text: "The recipe stays in the author's own account and any other groups. Only this group loses it.",
      action: 'Take out of group',
      danger: true,
      run: () => removeRecipeFromGroup(g.id, r.id),
    }).then((ok) => {
      if (!ok) return
      toast('Recipe taken out of the group')
      reload()
    })

  const download = () => {
    setBusy('download')
    exportRecipes({ group_id: g.id })
      .then((bytes) => {
        saveFile(bytes, `${g.name} recipes.xlsx`)
        toast('Spreadsheet downloaded')
      })
      .catch((e) => toastError(e, 'The spreadsheet could not be made.'))
      .finally(() => setBusy(''))
  }

  const remove = () =>
    confirm({
      title: 'Delete this group?',
      text: "This can't be undone. Type the group's name to confirm.",
      action: 'Delete group',
      danger: true,
      typed: g.name,
      typedLabel: "Type the group's name to confirm",
      run: () => deleteGroup(g.id),
    }).then((ok) => {
      if (!ok) return
      toast(`${g.name} was deleted`)
      navigate('/admin/groups')
    })

  return (
    <>
      <div>
        <Link className="a-crumb" to="/admin/groups">
          ← Family groups
        </Link>
        <div className="a-head">
          <div style={{ minWidth: 0 }}>
            <h1>{g.name}</h1>
            <p className="a-sub">
              Started {formatDate(g.created_at)}
              {owner ? ` · owner ${label(owner)}` : ''} · {members.length}{' '}
              {members.length === 1 ? 'member' : 'members'} · {recipes.length}{' '}
              {recipes.length === 1 ? 'recipe' : 'recipes'}
            </p>
          </div>
          <div className="a-btn-row">
            <button type="button" className="a-btn" onClick={rename}>
              Rename
            </button>
            {g.inviteLink ? (
              <button type="button" className="a-btn" onClick={copyInvite}>
                Copy invite link
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="a-grid2">
        <section className="a-panel">
          <h2>Members</h2>
          {members.length ? (
            <div className="a-table-wrap">
              <table className="a-table">
                <tbody>
                  {members.map((u) => {
                    const isOwner = Number(u.pivot?.role) === OWNER
                    return (
                      <tr
                        className="a-click"
                        key={u.id}
                        onClick={() => navigate(`/admin/people/${u.id}`)}
                      >
                        <td className="a-who">
                          <b>{label(u)}</b>
                          <span>
                            {ROLE_LABEL[Number(u.pivot?.role)] || 'Member'}
                            {u.pivot?.joined_at ? ` · joined ${formatDate(u.pivot.joined_at)}` : ''}
                          </span>
                        </td>
                        <td className="a-right">
                          {isOwner ? (
                            <span
                              className="a-muted a-small-text"
                              style={{
                                whiteSpace: 'normal',
                                display: 'inline-block',
                                maxWidth: 150,
                              }}
                            >
                              To remove the owner, make someone else owner first
                            </span>
                          ) : (
                            <div className="a-btn-row" style={{ justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                className="a-btn a-small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  makeOwner(u)
                                }}
                              >
                                Make owner
                              </button>
                              <button
                                type="button"
                                className="a-btn a-small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  kick(u)
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="a-empty">Nobody is in this group.</p>
          )}
        </section>

        <section className="a-panel">
          <div className="a-panel-head">
            <h2>Recipes</h2>
            {recipes.length ? (
              <button
                type="button"
                className="a-btn a-small"
                disabled={busy === 'download'}
                aria-disabled={busy === 'download'}
                onClick={download}
              >
                {busy === 'download' ? 'Preparing…' : 'Download all'}
              </button>
            ) : null}
          </div>
          {recipes.length ? (
            <div className="a-table-wrap">
              <table className="a-table">
                <tbody>
                  {recipes.map((r) => (
                    <tr
                      className="a-click"
                      key={r.id}
                      onClick={() => navigate(`/admin/recipes/${r.id}`)}
                    >
                      <td className="a-who">
                        <b>{r.name || 'Untitled recipe'}</b>
                        {r.author_name ? <span>by {r.author_name}</span> : null}
                      </td>
                      <td className="a-right">
                        <button
                          type="button"
                          className="a-btn a-small"
                          onClick={(e) => {
                            e.stopPropagation()
                            removeRecipe(r)
                          }}
                        >
                          Take out
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="a-empty">No recipes in this group yet.</p>
          )}
        </section>
      </div>

      <section className="a-panel a-danger-zone">
        <h2>Delete this group</h2>
        <p className="a-muted">
          Removes the group for all {members.length} {members.length === 1 ? 'member' : 'members'}.
          Recipes stay in each author's own account.
        </p>
        <div>
          <button type="button" className="a-btn a-danger" onClick={remove}>
            Delete group
          </button>
        </div>
      </section>
    </>
  )
}

export default Group
